// 商城管理 — bundle 编排器详情页 (benefit-bundle-storefront 组4).
//
// 一个 bundle = 固定项[](购买即得) + 选择组[](N选一, 选后才履约). 运营在此:
//   - 加固定项 / 加选择组
//   - 往槽里挂候选(从已有 benefit 产品挑, 或现建, 图片型可传海报一键建)
//   - 选择组显示"最贵候选成本"提示(design Risks: 成本极差按均值定价会亏)
//   - 上架(后端校验: 固定项恰好1候选、选择组≥1候选)

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import { PlusOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import {
  getBundle,
  updateBundle,
  addSlot,
  deleteSlot,
  addCandidate,
  deleteCandidate,
  listBenefitProducts,
  createBenefitProduct,
  createImageProduct,
  maxCandidateCost,
  FULFILL_LABEL,
  type BundleTree,
  type BundleSlot,
  type BenefitProduct,
  type FulfillType,
} from '@/api/bundles';

const yuan = (cents: number) => `¥${(cents / 100).toFixed(2)}`;

export function MallDetailPage() {
  const { id } = useParams<{ id: string }>();
  const bundleId = Number(id);
  const [tree, setTree] = useState<BundleTree | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    setLoading(true);
    getBundle(bundleId)
      .then(setTree)
      .catch((e) => message.error(e instanceof Error ? e.message : '加载失败'))
      .finally(() => setLoading(false));
  }, [bundleId]);
  useEffect(reload, [reload]);

  const [slotModal, setSlotModal] = useState<'fixed' | 'choice' | null>(null);
  const [pickSlot, setPickSlot] = useState<BundleSlot | null>(null);

  const toggleStatus = async () => {
    if (!tree) return;
    const next = tree.status === 'active' ? 'inactive' : 'active';
    try {
      await updateBundle(tree.id, { status: next });
      message.success(next === 'active' ? '已上架' : '已下架');
      reload();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '操作失败(上架需固定项恰好1候选、选择组≥1候选)');
    }
  };

  if (loading && !tree) {
    return <Spin style={{ display: 'block', marginTop: 80 }} />;
  }
  if (!tree) return <Empty description="bundle 不存在" />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space>
        <Link to="/mall">← 返回列表</Link>
      </Space>

      <Card
        title={
          <Space>
            {tree.name}
            <Tag color={tree.status === 'active' ? 'green' : tree.status === 'draft' ? 'default' : 'red'}>
              {tree.status === 'active' ? '已上架' : tree.status === 'draft' ? '草稿' : '已下架'}
            </Tag>
          </Space>
        }
        extra={
          <Popconfirm
            title={tree.status === 'active' ? '下架?' : '上架?'}
            description={tree.status === 'active' ? '推广页停止展示, 已购不受影响' : '后端校验结构后上架'}
            onConfirm={toggleStatus}
          >
            <Button type={tree.status === 'active' ? 'default' : 'primary'}>
              {tree.status === 'active' ? '下架' : '上架'}
            </Button>
          </Popconfirm>
        }
      >
        <Descriptions size="small" column={3}>
          <Descriptions.Item label="月付价">{yuan(tree.monthly_price_cents)}</Descriptions.Item>
          <Descriptions.Item label="slug">{tree.slug || '-'}</Descriptions.Item>
          <Descriptions.Item label="App">#{tree.app_id}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Space>
        <Button icon={<PlusOutlined />} onClick={() => setSlotModal('fixed')}>
          加固定项
        </Button>
        <Button icon={<PlusOutlined />} onClick={() => setSlotModal('choice')}>
          加选择组
        </Button>
      </Space>

      {tree.slots.length === 0 ? (
        <Empty description="还没有槽 — 加固定项(购买即得)或选择组(N选一, 选后才履约)" />
      ) : (
        tree.slots.map((slot) => (
          <SlotCard key={slot.id} slot={slot} onAddCandidate={() => setPickSlot(slot)} onChanged={reload} />
        ))
      )}

      {slotModal && (
        <SlotCreateModal
          bundleId={tree.id}
          kind={slotModal}
          onClose={() => setSlotModal(null)}
          onCreated={() => {
            setSlotModal(null);
            reload();
          }}
        />
      )}
      {pickSlot && (
        <CandidatePickerModal
          appId={tree.app_id}
          slot={pickSlot}
          onClose={() => setPickSlot(null)}
          onAdded={() => {
            setPickSlot(null);
            reload();
          }}
        />
      )}
    </Space>
  );
}

// ---------------------------------------------------------------------------
// 槽卡片
// ---------------------------------------------------------------------------

function SlotCard({
  slot,
  onAddCandidate,
  onChanged,
}: {
  slot: BundleSlot;
  onAddCandidate: () => void;
  onChanged: () => void;
}) {
  const isChoice = slot.kind === 'choice';
  const fixedFull = slot.kind === 'fixed' && slot.candidates.length >= 1;

  const removeCandidate = async (candidateId: number) => {
    try {
      await deleteCandidate(candidateId);
      onChanged();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '删除失败');
    }
  };

  const removeSlot = async () => {
    try {
      await deleteSlot(slot.id);
      onChanged();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '删除失败');
    }
  };

  return (
    <Card
      size="small"
      title={
        <Space>
          <Tag color={isChoice ? 'purple' : 'blue'}>{isChoice ? '选择组' : '固定项'}</Tag>
          {slot.name || (isChoice ? `${slot.candidates.length}选${slot.select_count}` : '购买即得')}
        </Space>
      }
      extra={
        <Space>
          <Button
            type="link"
            size="small"
            icon={<PlusOutlined />}
            disabled={fixedFull}
            onClick={onAddCandidate}
          >
            加候选
          </Button>
          <Popconfirm title="删除该槽?" description="连同其候选一并移除" onConfirm={removeSlot}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删槽
            </Button>
          </Popconfirm>
        </Space>
      }
    >
      {isChoice && (
        <Space size="large" style={{ marginBottom: 8 }}>
          <Typography.Text type="secondary">
            N选{slot.select_count} · 未选 {slot.expire_after_hours}h 后作废(第 {slot.reminder_after_hours}h 提醒)
          </Typography.Text>
          {slot.candidates.length > 0 && (
            <Tag color="orange">
              最贵候选成本 {yuan(maxCandidateCost(slot))} — bundle 定价按此兜底毛利
            </Tag>
          )}
        </Space>
      )}
      {slot.candidates.length === 0 ? (
        <Alert
          type={isChoice ? 'warning' : 'error'}
          showIcon
          message={isChoice ? '选择组还没有候选 — 上架前必须≥1个' : '固定项还没有候选 — 上架前必须恰好1个'}
        />
      ) : (
        <Table<BundleSlot['candidates'][number]>
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={slot.candidates}
          columns={[
            { title: '产品', dataIndex: 'product_name' },
            {
              title: '类型',
              dataIndex: 'fulfill_type',
              render: (v: FulfillType) => <Tag>{FULFILL_LABEL[v] ?? v}</Tag>,
            },
            { title: '成本', dataIndex: 'cost_cents', align: 'right', render: (c: number) => yuan(c) },
            {
              title: '',
              key: 'op',
              width: 60,
              render: (_, c) => (
                <Popconfirm title="移除该候选?" onConfirm={() => removeCandidate(c.id)}>
                  <Button type="link" size="small" danger>
                    移除
                  </Button>
                </Popconfirm>
              ),
            },
          ]}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 加槽 modal
// ---------------------------------------------------------------------------

function SlotCreateModal({
  bundleId,
  kind,
  onClose,
  onCreated,
}: {
  bundleId: number;
  kind: 'fixed' | 'choice';
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form] = Form.useForm();
  const submit = async () => {
    const v = await form.validateFields();
    try {
      await addSlot(bundleId, {
        kind,
        name: v.name || '',
        ...(kind === 'choice'
          ? {
              select_count: v.select_count || 1,
              reminder_after_hours: v.reminder_after_hours ?? 168,
              expire_after_hours: v.expire_after_hours ?? 336,
            }
          : {}),
      });
      message.success('已添加');
      onCreated();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '添加失败');
    }
  };
  return (
    <Modal
      title={kind === 'choice' ? '加选择组' : '加固定项'}
      open
      onOk={submit}
      onCancel={onClose}
      okText="添加"
      cancelText="取消"
      destroyOnClose
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item name="name" label="名称(可空)">
          <Input placeholder={kind === 'choice' ? '如: 生活娱乐七选一' : '如: 幻旅志会员'} />
        </Form.Item>
        {kind === 'choice' && (
          <>
            <Form.Item name="select_count" label="可选数量" initialValue={1}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
            <Space>
              <Form.Item name="reminder_after_hours" label="提醒(小时)" initialValue={168}>
                <InputNumber min={0} />
              </Form.Item>
              <Form.Item name="expire_after_hours" label="作废(小时)" initialValue={336}>
                <InputNumber min={0} />
              </Form.Item>
            </Space>
            <Alert
              type="info"
              showIcon
              message="未选过期 = 作废(不帮选默认); 到期前先提醒"
            />
          </>
        )}
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 候选选择器(挑已有产品 或 现建)
// ---------------------------------------------------------------------------

function CandidatePickerModal({
  appId,
  slot,
  onClose,
  onAdded,
}: {
  appId: number;
  slot: BundleSlot;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [products, setProducts] = useState<BenefitProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const loadProducts = useCallback(() => {
    setLoading(true);
    listBenefitProducts(appId)
      .then((res) => setProducts(res.data))
      .catch((e) => message.error(e instanceof Error ? e.message : '加载产品失败'))
      .finally(() => setLoading(false));
  }, [appId]);
  useEffect(loadProducts, [loadProducts]);

  const existingIDs = new Set(slot.candidates.map((c) => c.product_id));

  const pick = async (p: BenefitProduct) => {
    try {
      await addCandidate(slot.id, p.id);
      message.success('已加为候选');
      onAdded();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '添加失败(固定项只能1个候选)');
    }
  };

  return (
    <Modal title="挑选候选产品" open onCancel={onClose} footer={null} width={640} destroyOnClose>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
          新建产品
        </Button>
        <Typography.Text type="secondary">或从下方已有产品挑选</Typography.Text>
      </Space>
      <Table<BenefitProduct>
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={products}
        pagination={{ pageSize: 8 }}
        columns={[
          { title: '产品', dataIndex: 'name' },
          {
            title: '类型',
            dataIndex: 'fulfill_type',
            render: (v: FulfillType) => <Tag>{FULFILL_LABEL[v] ?? v}</Tag>,
          },
          { title: '成本', dataIndex: 'cost_cents', align: 'right', render: (c: number) => yuan(c) },
          {
            title: '',
            key: 'op',
            width: 80,
            render: (_, p) =>
              existingIDs.has(p.id) ? (
                <Tag>已在此槽</Tag>
              ) : (
                <Button type="link" size="small" onClick={() => pick(p)}>
                  选入
                </Button>
              ),
          },
        ]}
      />
      {creating && (
        <ProductCreateModal
          appId={appId}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            loadProducts();
          }}
        />
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 建产品(4 类; 图片型走海报上传一键建)
// ---------------------------------------------------------------------------

function ProductCreateModal({
  appId,
  onClose,
  onCreated,
}: {
  appId: number;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form] = Form.useForm();
  const [fulfillType, setFulfillType] = useState<FulfillType>('cardkey');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const v = await form.validateFields();
    setSubmitting(true);
    try {
      if (fulfillType === 'image') {
        if (!file) {
          message.error('请上传海报图');
          setSubmitting(false);
          return;
        }
        await createImageProduct(file, {
          app_id: appId,
          title: v.name,
          cost_cents: Math.round((v.cost_yuan || 0) * 100),
        });
      } else {
        await createBenefitProduct({
          app_id: appId,
          fulfill_type: fulfillType,
          name: v.name,
          cost_cents: Math.round((v.cost_yuan || 0) * 100),
          config: {
            sku_code: v.sku_code,
            ...(v.duration_days ? { duration_days: v.duration_days } : {}),
          },
        });
      }
      message.success('已创建');
      onCreated();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="新建产品"
      open
      onOk={submit}
      confirmLoading={submitting}
      onCancel={onClose}
      okText="创建"
      cancelText="取消"
      destroyOnClose
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item label="履约类型">
          <Select
            value={fulfillType}
            onChange={setFulfillType}
            options={(['cardkey', 'topup', 'membership', 'image'] as FulfillType[]).map((t) => ({
              value: t,
              label: FULFILL_LABEL[t],
            }))}
          />
        </Form.Item>
        <Form.Item name="name" label={fulfillType === 'image' ? '标题' : '产品名'} rules={[{ required: true }]}>
          <Input placeholder={fulfillType === 'image' ? '如: 花猪出行大礼包' : '如: 爱奇艺黄金会员月卡'} />
        </Form.Item>
        <Form.Item name="cost_yuan" label="成本(元)" initialValue={0}>
          <InputNumber min={0} precision={2} style={{ width: '100%' }} addonBefore="¥" />
        </Form.Item>

        {fulfillType === 'image' ? (
          <>
            <Form.Item label="海报图(含小程序码)" required>
              <Upload
                accept="image/*"
                maxCount={1}
                beforeUpload={(f) => {
                  setFile(f);
                  return false; // 手动上传
                }}
                onRemove={() => setFile(null)}
              >
                <Button icon={<UploadOutlined />}>选择海报</Button>
              </Upload>
            </Form.Item>
            <Divider style={{ margin: '8px 0' }} />
            <Typography.Text type="secondary">
              图片型零成本无库存: 用户长按识别海报里的小程序码直接跳对方小程序.
            </Typography.Text>
          </>
        ) : (
          <>
            <Form.Item
              name="sku_code"
              label="SKU code(指向既有 skus 的供货配置)"
              rules={[{ required: true, message: '请填 sku_code' }]}
            >
              <Input placeholder="如: iqiyi_gold_monthly" />
            </Form.Item>
            {fulfillType === 'cardkey' && (
              <Form.Item name="duration_days" label="卡密有效期(天, 从选择起算; 供应商未回则用此)">
                <InputNumber min={1} style={{ width: '100%' }} placeholder="默认 30" />
              </Form.Item>
            )}
          </>
        )}
      </Form>
    </Modal>
  );
}
